import React, { useState, useRef, useEffect } from 'react';
import { Eye, Edit, MoreVertical, Download, Search, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import UserDetailModal from './UserDetailModal';
import { User } from '@/services/InstructorService/InstructorUsersService';
import useInstructorUsersStore from '@/stores/InstructorStore/InstructorUsersStore';
import './UserDirectoryTab.css';

interface UserDirectoryTabProps {
  users: User[];
}

const UserDirectoryTab: React.FC<UserDirectoryTabProps> = ({ users }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All Department');
  const [selectedStatus, setSelectedStatus] = useState('All Status');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    updateUser,
    deactivateUser,
    reactivateUser,
    fetchUserById,
    selectedUser: fullUserDetails,
    updating,
    deactivating,
    reactivating
  } = useInstructorUsersStore();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleViewUser = async (user: User) => {
    // Fetch full user details with courses and learning history
    const userDetails = await fetchUserById(user.id);
    if (userDetails) {
      setSelectedUser(user); // Keep for modal opening logic
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleDeactivateUser = (user: User) => {
    setUserToDeactivate(user);
    setShowDeactivateModal(true);
    setOpenMenuId(null);
  };

  const confirmDeactivate = async () => {
    if (!userToDeactivate) return;

    if (!deactivationReason.trim()) {
      toast.error('Deactivation reason is required');
      return;
    }

    const result = await deactivateUser(userToDeactivate.id, deactivationReason);
    if (result.success) {
      toast.success(result.message);
      setShowDeactivateModal(false);
      setUserToDeactivate(null);
      setDeactivationReason('');
    } else {
      toast.error(result.message);
    }
  };

  const handleReactivateUser = (user: User) => {
    setUserToReactivate(user);
    setShowReactivateModal(true);
    setOpenMenuId(null);
  };

  const confirmReactivate = async () => {
    if (!userToReactivate) return;

    const result = await reactivateUser(userToReactivate.id);
    if (result.success) {
      toast.success(result.message);
      setShowReactivateModal(false);
      setUserToReactivate(null);
    } else {
      toast.error(result.message);
    }
  };

  const handleEnrollCourse = (courseName: string) => {
    console.log(`Enrolling user in: ${courseName}`);
    // Add enrollment logic here
  };

  const toggleMenu = (userId: string) => {
    setOpenMenuId(openMenuId === userId ? null : userId);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'All Department' || user.department === selectedDepartment;
    const matchesStatus = selectedStatus === 'All Status' || user.status === selectedStatus;
    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="trainer-user-directory">
      <div className="trainer-user-directory-controls">
        <div className="trainer-user-directory-search">
          <Search className="trainer-user-directory-search-icon" size={18} />
          <input
            type="text"
            placeholder="Search users by name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="trainer-user-directory-select"
          value={selectedDepartment}
          onChange={(e) => setSelectedDepartment(e.target.value)}
        >
          <option>All Department</option>
          <option>Engineering</option>
          <option>Sales</option>
          <option>HR</option>
          <option>Design</option>
        </select>
        <select
          className="trainer-user-directory-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>
        <button className="trainer-user-directory-export-btn">
          <Download size={18} />
          Export
        </button>
      </div>

      <div className="trainer-user-directory-table-container">
        <table className="trainer-user-directory-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Department</th>
              <th>Job Title</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Course</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="trainer-user-directory-user-cell">
                    <div className="trainer-user-directory-avatar">{user.initials}</div>
                    <span>{user.name}</span>
                  </div>
                </td>
                <td>{user.department}</td>
                <td>{user.jobTitle}</td>
                <td>
                  <span className={`trainer-user-directory-status-badge ${user.status.toLowerCase()}`}>
                    {user.status}
                  </span>
                </td>
                <td>{user.lastLogin}</td>
                <td>
                  <div className="trainer-user-directory-course-info">
                    <span className="trainer-user-directory-course-completed">{user.coursesCompleted} completed</span>
                    <span className="trainer-user-directory-course-active">{user.coursesActive} active</span>
                  </div>
                </td>
                <td>
                  <div className="trainer-user-directory-actions" style={{ position: 'relative' }}>
                    <button
                      className="trainer-user-directory-action-btn"
                      onClick={() => handleViewUser(user)}
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      className="trainer-user-directory-action-btn"
                      onClick={() => handleEditUser(user)}
                      title="Edit User"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      className="trainer-user-directory-action-btn"
                      onClick={() => toggleMenu(user.id)}
                      title="More Options"
                    >
                      <MoreVertical size={18} />
                    </button>

                    {openMenuId === user.id && (
                      <div
                        ref={menuRef}
                        className="trainer-user-directory-dropdown-menu"
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: '100%',
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          zIndex: 1000,
                          minWidth: '160px',
                          marginTop: '4px'
                        }}
                      >
                        {user.status === 'Active' ? (
                          <button
                            onClick={() => handleDeactivateUser(user)}
                            disabled={deactivating}
                            style={{
                              width: '100%',
                              padding: '10px 16px',
                              textAlign: 'left',
                              border: 'none',
                              background: 'none',
                              cursor: deactivating ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              color: '#dc2626'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Trash2 size={16} />
                            {deactivating ? 'Deactivating...' : 'Deactivate'}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivateUser(user)}
                            disabled={reactivating}
                            style={{
                              width: '100%',
                              padding: '10px 16px',
                              textAlign: 'left',
                              border: 'none',
                              background: 'none',
                              cursor: reactivating ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              color: '#16a34a'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <RefreshCw size={16} />
                            {reactivating ? 'Reactivating...' : 'Reactivate'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="trainer-user-directory-pagination">
          <div className="trainer-user-directory-pagination-info">
            <span>Showing</span>
            <select
              className="trainer-user-directory-pagination-select"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>of {filteredUsers.length}</span>
          </div>

          <div className="trainer-user-directory-pagination-buttons">
            <button
              className="trainer-user-directory-page-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`trainer-user-directory-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="trainer-user-directory-page-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {selectedUser && fullUserDetails && (
        <UserDetailModal
          user={{
            id: fullUserDetails.id,
            name: fullUserDetails.name,
            email: fullUserDetails.email || '',
            jobTitle: fullUserDetails.jobTitle,
            department: fullUserDetails.department,
            initials: fullUserDetails.initials,
            progress: fullUserDetails.progress || 0,
            phone: fullUserDetails.phone || '',
            location: fullUserDetails.location || '',
            manager: fullUserDetails.manager || '',
            joinDate: fullUserDetails.joinDate || '',
            lastLogin: fullUserDetails.lastLogin,
            completedCourses: fullUserDetails.completedCourses || 0,
            inProgressCourses: fullUserDetails.inProgressCourses || 0,
          }}
          courses={fullUserDetails.courses || []}
          learningHistory={fullUserDetails.learningHistory || []}
          trainerActivities={fullUserDetails.trainerActivities || []}
          onClose={() => {
            setSelectedUser(null);
          }}
          onEnrollCourse={handleEnrollCourse}
        />
      )}

      {showEditModal && editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
          onSave={async (updatedData) => {
            const result = await updateUser(editingUser.id, updatedData);
            if (result.success) {
              toast.success(result.message);
              setShowEditModal(false);
              setEditingUser(null);
            } else {
              toast.error(result.message);
            }
          }}
          updating={updating}
        />
      )}

      {showDeactivateModal && userToDeactivate && (
        <ConfirmModal
          title="Deactivate Learner"
          message={`Are you sure you want to deactivate ${userToDeactivate.name}?`}
          confirmText={deactivating ? 'Deactivating...' : 'Deactivate'}
          confirmColor="#dc2626"
          onConfirm={confirmDeactivate}
          onCancel={() => {
            setShowDeactivateModal(false);
            setUserToDeactivate(null);
            setDeactivationReason('');
          }}
          disabled={deactivating}
          showReasonInput
          reason={deactivationReason}
          onReasonChange={setDeactivationReason}
          reasonPlaceholder="Please enter a reason for deactivation"
        />
      )}

      {showReactivateModal && userToReactivate && (
        <ConfirmModal
          title="Reactivate Learner"
          message={`Are you sure you want to reactivate ${userToReactivate.name}?`}
          confirmText={reactivating ? 'Reactivating...' : 'Reactivate'}
          confirmColor="#16a34a"
          onConfirm={confirmReactivate}
          onCancel={() => {
            setShowReactivateModal(false);
            setUserToReactivate(null);
          }}
          disabled={reactivating}
        />
      )}
    </div>
  );
};

// Edit User Modal Component
interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onSave: (data: { firstName?: string; lastName?: string; department?: string; jobTitle?: string; status?: 'Active' | 'Inactive' }) => Promise<void>;
  updating: boolean;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSave, updating }) => {
  const [firstName, setFirstName] = useState(user.name.split(' ')[0] || '');
  const [lastName, setLastName] = useState(user.name.split(' ').slice(1).join(' ') || '');
  const [department, setDepartment] = useState(user.department);
  const [jobTitle, setJobTitle] = useState(user.jobTitle);
  const [status, setStatus] = useState<'Active' | 'Inactive'>(user.status);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({ firstName, lastName, department, jobTitle, status });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '90%',
          maxWidth: '500px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>Edit Learner</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Department</label>
            <input
              type="text"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Job Title</label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '14px' }}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                backgroundColor: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updating}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: '#f97316',
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                cursor: updating ? 'not-allowed' : 'pointer',
                opacity: updating ? 0.6 : 1
              }}
            >
              {updating ? 'Updating...' : 'Update Learner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Confirm Modal Component
interface ConfirmModalProps {
  title: string;
  message: string;
  confirmText: string;
  confirmColor: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
  showReasonInput?: boolean;
  reason?: string;
  onReasonChange?: (value: string) => void;
  reasonPlaceholder?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  confirmText,
  confirmColor,
  onConfirm,
  onCancel,
  disabled = false,
  showReasonInput = false,
  reason = '',
  onReasonChange,
  reasonPlaceholder = ''
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '90%',
          maxWidth: '450px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>{title}</h2>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: showReasonInput ? '16px' : '24px' }}>
          {message}
        </p>

        {showReasonInput && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Reason *
            </label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange?.(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              backgroundColor: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={disabled}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: confirmColor,
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDirectoryTab;
